/**
 * N1 w panelu (prompt 01/08, 2026-10-10) — zapis rekordu lokalu z formularza admina z kontrolą wersji.
 *
 * Tablet od #371 wysyła `expectedUpdatedAt` (wersja rekordu, którą widział) i dostaje 409
 * RECORD_CHANGED, gdy ktoś zapisał później. Panel tego nie robił: zapis z formularza otwartego przed
 * synchronizacją tabletu po cichu nadpisywał status, zalecenia i próby zapisane w terenie.
 * Teraz formularz odsyła `updatedAt` wczytanego rekordu (to samo pole co mobilka:
 * mobile/src/utils/serverRecord.ts → serverUpdatedAt), a konflikt rozstrzyga człowiek.
 */
import type { InspectionAttempt } from '../types/inspection';
import { attemptNumber, formatAttemptDate, romanTerm } from './attempts';
import { STATUS_LABELS } from '../ds/StatusMap';
import { HEAT_TYPES_WITHOUT_CHARACTER, resolveHeatCharacter, resolveHeatSourceType } from '../ds/HeatSourceLabels';

/**
 * Wersja „nie widziałem żadnego rekordu" — formularz otwarty, gdy rekordu tego przeglądu jeszcze nie
 * było (albo odczyt się nie udał). Rekord założony w międzyczasie przez tablet jest od niej nowszy, więc
 * serwer porównuje treść (sonda N1) i odpowiada 409 zamiast nadpisać; identyczna treść = 200 bez zapisu.
 * Gdy rekordu nadal nie ma, serwer pola nie używa (INSERT).
 */
export const NO_RECORD_VERSION = new Date(0).toISOString();

/** Rekord lokalu z serwera — pola, których potrzebuje porównanie (kształt GET /apartment-inspections). */
export interface ServerApartmentRecord {
  id?: string;
  updatedAt?: string | null;
  savedBy?: string | null;
  /** Pola tylko-do-odczytu w panelu — „Zapisz moją wersję" bierze je z serwera, nie z formularza. */
  protocolNumber?: string | null;
  inspectionDate?: string | null;
  inspectionStatus?: string | null;
  ownerName?: string | null;
  recommendations?: string | null;
  attempts?: InspectionAttempt[] | null;
  /** Źródła ciepła lokalu (JSONB z tabletu) i meta-flagi — porównywane, gdy formularz je wysyła. */
  heatSources?: unknown[] | null;
  usesBuildingHeat?: boolean | null;
  noHeatSource?: boolean | null;
}

export interface RecordChangedError {
  status: 409;
  code: 'RECORD_CHANGED';
  record?: ServerApartmentRecord;
}

/** Odpowiedź 409 „rekord lokalu zmieniono na innym urządzeniu" (apartmentInspectionsApi.createOrUpdate). */
export function isRecordChangedError(error: unknown): error is RecordChangedError {
  const e = error as { status?: number; code?: string } | null;
  return !!e && e.status === 409 && e.code === 'RECORD_CHANGED';
}

/** Wersja do odesłania w `expectedUpdatedAt`: `updatedAt` rekordu, a bez rekordu — NO_RECORD_VERSION. */
export function expectedVersionOf(record: ServerApartmentRecord | null | undefined): string {
  return (record && record.updatedAt) || NO_RECORD_VERSION;
}

/** Treść formularza panelu, którą porównujemy z wersją z serwera. */
export interface AdminRecordContent {
  inspectionStatus: string;
  ownerName: string;
  recommendations: string;
  attempts: InspectionAttempt[];
  /**
   * Część „Źródła ciepła" w kształcie, w jakim idzie w zapisie (utils/apartmentHeatSources.heatPayload):
   * tylko pola zmienione przez admina. Pole nieobecne = zapis go nie dotyka, więc nie ma czego porównywać.
   */
  heat?: { heatSources?: unknown[]; usesBuildingHeat?: boolean; noHeatSource?: boolean };
}

export interface RecordDiffRow {
  label: string;
  server: string;
  mine: string;
}

const EMPTY = '—';

function statusText(s?: string | null): string {
  return s ? STATUS_LABELS[s] ?? s : EMPTY;
}

function plainText(s?: string | null): string {
  const t = (s ?? '').trim();
  return t || EMPTY;
}

/** „I termin 08.10.2026, II termin 15.10.2026" — etykiety jak w historii prób formularza. */
function attemptsText(list?: InspectionAttempt[] | null): string {
  if (!Array.isArray(list) || list.length === 0) return EMPTY;
  return list
    .map((a, i) => {
      const roman = romanTerm(attemptNumber(a, i));
      return `${roman ? `${roman} termin` : 'Wizyta'} ${formatAttemptDate(a)}`;
    })
    .join(', ');
}

type HeatLike = { type?: unknown; heatCharacter?: unknown; heat_character?: unknown; params?: unknown };

/** „Kominek (M.O.P.), Piec kaflowy" — skróty ze słownika (ds/HeatSourceLabels). */
function heatSourcesText(list?: unknown[] | null): string {
  const items = (Array.isArray(list) ? list : []).filter((h): h is HeatLike => !!h && typeof h === 'object');
  if (items.length === 0) return 'brak';
  return items
    .map((h) => {
      const key = typeof h.type === 'string' ? h.type : '';
      const type = resolveHeatSourceType(key) || '?';
      const raw = h.heatCharacter ?? h.heat_character;
      // Typy bez „Charakteru" w CEEB — wartość techniczna 'heating' nic nie mówi, nie pokazujemy.
      const character = typeof raw === 'string' && !HEAT_TYPES_WITHOUT_CHARACTER.includes(key) ? resolveHeatCharacter(raw) : '';
      return character ? `${type} (${character})` : type;
    })
    .join(', ');
}

function sortedParams(p: unknown): unknown {
  if (!p || typeof p !== 'object') return p ?? null;
  const o = p as Record<string, unknown>;
  return Object.keys(o).sort().map((k) => [k, o[k]]);
}

/** Klucz porównania jak na tablecie (mobile utils/recordConflict.heatSourcesKey): typ, charakter, params. */
function heatSourcesKey(list?: unknown[] | null): string {
  const items = (Array.isArray(list) ? list : []).filter((h): h is HeatLike => !!h && typeof h === 'object');
  return JSON.stringify(items.map((h) => [h.type || '', h.heatCharacter || h.heat_character || '', sortedParams(h.params)]));
}

/** Meta-flagi źródeł lokalu — te same słowa co okno konfliktu na tablecie. */
function heatFlagsText(connected?: boolean | null, noSource?: boolean | null): string {
  const parts: string[] = [];
  if (connected) parts.push('podłączony do wspólnego źródła');
  if (noSource) parts.push('brak źródła ciepła');
  return parts.length ? parts.join(', ') : EMPTY;
}

/**
 * Pola widoczne w formularzu panelu, które różnią się między wersją z serwera a formularzem.
 * Pusta lista = różnica dotyczy danych, których zapis panelu nie niesie (np. data wizyty, podpis, źródła
 * ciepła nieruszane w formularzu).
 */
export function recordDiffRows(server: ServerApartmentRecord, mine: AdminRecordContent): RecordDiffRow[] {
  const rows: RecordDiffRow[] = [
    { label: 'Status', server: statusText(server.inspectionStatus), mine: statusText(mine.inspectionStatus) },
    { label: 'Oznaczenie specjalne lokalu', server: plainText(server.ownerName), mine: plainText(mine.ownerName) },
    { label: 'Uwagi i zalecenia', server: plainText(server.recommendations), mine: plainText(mine.recommendations) },
    { label: 'Próby dostępu', server: attemptsText(server.attempts), mine: attemptsText(mine.attempts) },
  ];
  const heat = mine.heat;
  if (heat && heat.heatSources !== undefined && heatSourcesKey(server.heatSources) !== heatSourcesKey(heat.heatSources)) {
    rows.push({ label: 'Źródła ciepła lokalu', server: heatSourcesText(server.heatSources), mine: heatSourcesText(heat.heatSources) });
  }
  if (heat && (heat.usesBuildingHeat !== undefined || heat.noHeatSource !== undefined)) {
    // Flaga, której zapis nie niesie, zostaje na serwerze — po zapisie będzie taka jak tam.
    const mineUses = heat.usesBuildingHeat !== undefined ? heat.usesBuildingHeat : server.usesBuildingHeat;
    const mineNone = heat.noHeatSource !== undefined ? heat.noHeatSource : server.noHeatSource;
    rows.push({
      label: 'Ogrzewanie lokalu',
      server: heatFlagsText(server.usesBuildingHeat, server.noHeatSource),
      mine: heatFlagsText(mineUses, mineNone),
    });
  }
  return rows.filter((r) => r.server !== r.mine);
}
